import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import ImagenProducto from './ImagenProducto';
import { makeQrValue } from '../utils/qrCodes';
import { decodeHtml } from '../utils/decodeHtml';
import { generateAndDownloadLabels } from '../utils/printLabel';
import { loadAllShortNames, labelKey, useLabelPrefsSync } from '../utils/labelNames';
import LabelBatchReviewModal from './LabelBatchReviewModal';
import LabelThumb from './LabelThumb';
import { QRModal } from './QRCodeDisplay';
import { productPlaceholder } from '../utils/productPlaceholder';
import './LabelingTab.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const PLACEHOLDER = productPlaceholder(40, 40);
const LS_KEY = 'catalog_labeled';

function fmtPrice(p) {
  if (!p) return null;
  return '$' + parseFloat(p).toLocaleString('es-CO');
}

function loadLabeledMap() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); }
  catch { return {}; }
}

// Aplana productos a filas: una por variación (o una para productos simples).
function flattenProducts(products) {
  const rows = [];
  for (const p of products) {
    const productName = decodeHtml(p.name);
    const image = p.image || PLACEHOLDER;
    const categories = p.categories || [];

    if (p.variations && p.variations.length > 0) {
      for (const v of p.variations) {
        rows.push({
          key:           `var-${v.id}`,
          productId:     p.id,
          variationId:   v.id,
          productName,
          variationLabel: v.label || null,
          sku:           v.sku || p.sku || null,
          price:         v.price || p.price || null,
          stock:         v.stock_quantity != null ? v.stock_quantity : 0,
          image,
          categories,
        });
      }
    } else {
      rows.push({
        key:           `prod-${p.id}`,
        productId:     p.id,
        variationId:   null,
        productName,
        variationLabel: null,
        sku:           p.sku || null,
        price:         p.price || null,
        stock:         p.stock_quantity != null ? p.stock_quantity : 0,
        image,
        categories,
      });
    }
  }
  return rows;
}

export default function LabelingTab({ products, storeName, labelConfig }) {
  const [search,      setSearch]      = useState('');
  const [catFilter,   setCatFilter]   = useState('');
  const [labelFilter, setLabelFilter] = useState('all'); // 'all' | 'unlabeled' | 'labeled'
  const [selected,    setSelected]    = useState({});    // { [key]: number (qty) }
  const [labeledMap,  setLabeledMap]  = useState(loadLabeledMap);
  const [generating,  setGenerating]  = useState(false);
  const [lotsCache,   setLotsCache]   = useState({});    // { [key]: lots[] | 'loading' }
  const [selectedLot, setSelectedLot] = useState({});    // { [key]: loteNumero }
  const [reviewOpen,  setReviewOpen]  = useState(false);
  const [editorRow,   setEditorRow]   = useState(null);  // fila abierta en el editor individual
  const [namesVer,    setNamesVer]    = useState(0);      // bump para refrescar miniaturas tras editar
  const qrRefs = useRef({});

  // Sincroniza preferencias de etiqueta con el servidor; prefsVer cambia al llegar
  // datos frescos (p. ej. lo editado desde otro dispositivo) para refrescar miniaturas.
  const prefsVer = useLabelPrefsSync();

  // Mapa completo de nombres cortos (una sola lectura de localStorage por versión).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const shortMap = useMemo(() => loadAllShortNames(), [namesVer, prefsVer]);

  const rows = useMemo(() => flattenProducts(products), [products]);

  // Cuando se selecciona una fila por primera vez, carga sus lotes activos lazily.
  useEffect(() => {
    const keysToFetch = Object.keys(selected).filter(
      (k) => lotsCache[k] === undefined
    );
    if (keysToFetch.length === 0) return;
    // Marca como 'loading' y lanza los fetches en paralelo
    setLotsCache((prev) => {
      const next = { ...prev };
      keysToFetch.forEach((k) => { next[k] = 'loading'; });
      return next;
    });
    keysToFetch.forEach((key) => {
      const row = rows.find((r) => r.key === key);
      if (!row) return;
      const params = new URLSearchParams({ productId: String(row.productId) });
      if (row.variationId) params.set('variationId', String(row.variationId));
      fetch(`${API_URL}/api/lots/active?${params}`)
        .then((r) => r.ok ? r.json() : { lots: [] })
        .then((data) => {
          const lots = data.lots || [];
          setLotsCache((prev) => ({ ...prev, [key]: lots }));
          if (lots.length > 0) {
            setSelectedLot((prev) => ({ ...prev, [key]: lots[0].loteNumero }));
          }
        })
        .catch(() => setLotsCache((prev) => ({ ...prev, [key]: [] })));
    });
  }, [selected, lotsCache, rows]);

  const allCategories = useMemo(() => {
    const s = new Set();
    for (const r of rows) for (const c of r.categories) s.add(c);
    return [...s].sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r => {
      if (q) {
        const hit =
          r.productName.toLowerCase().includes(q) ||
          (r.variationLabel || '').toLowerCase().includes(q) ||
          (r.sku || '').toLowerCase().includes(q) ||
          r.categories.some(c => c.toLowerCase().includes(q));
        if (!hit) return false;
      }
      if (catFilter && !r.categories.includes(catFilter)) return false;
      if (labelFilter === 'labeled')   return !!labeledMap[r.key];
      if (labelFilter === 'unlabeled') return !labeledMap[r.key];
      return true;
    });
  }, [rows, search, catFilter, labelFilter, labeledMap]);

  // Filas seleccionadas (pueden NO estar en filteredRows si el filtro cambió)
  const selectedRows = useMemo(
    () => rows.filter(r => selected[r.key] !== undefined),
    [rows, selected]
  );

  const totalSelected = selectedRows.length;
  const totalLabels   = selectedRows.reduce((s, r) => s + (parseInt(selected[r.key]) || 0), 0);
  const hasSelection  = totalSelected > 0 && totalLabels > 0;

  const toggleRow = useCallback((key) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[key] !== undefined) delete next[key];
      else next[key] = 1;
      return next;
    });
  }, []);

  const setQty = useCallback((key, val) => {
    setSelected(prev => ({ ...prev, [key]: Math.max(0, parseInt(val) || 0) }));
  }, []);

  const selectAll = useCallback(() => {
    setSelected(prev => {
      const next = { ...prev };
      for (const r of filteredRows) {
        if (next[r.key] === undefined) next[r.key] = 1;
      }
      return next;
    });
  }, [filteredRows]);

  const clearAll = useCallback(() => setSelected({}), []);

  const applyStockQty = useCallback(() => {
    setSelected(prev => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        const row = rows.find(r => r.key === key);
        if (row) next[key] = Math.max(1, row.stock || 1);
      }
      return next;
    });
  }, [rows]);

  // Filas para el modal de revisión: una por variación seleccionada con qty y
  // lote resuelto (nombre/variación se editan dentro del modal).
  const reviewRows = useMemo(() => {
    return selectedRows.map((r) => {
      const qty = parseInt(selected[r.key]) || 0;
      if (qty <= 0) return null;
      const rowLots = Array.isArray(lotsCache[r.key]) ? lotsCache[r.key] : [];
      const chosenNum = selectedLot[r.key] || (rowLots[0]?.loteNumero ?? null);
      const saved = shortMap[labelKey(r.productId, r.variationId)] || {};
      return {
        key:            r.key,
        productId:      r.productId,
        variationId:    r.variationId,
        productName:    r.productName,
        variationLabel: r.variationLabel,
        sku:            r.sku,
        price:          fmtPrice(r.price),
        loteNumero:     chosenNum,
        layout:         saved.layout    || null,
        qrScale:        saved.qr        ?? 1,
        priceSize:      saved.priceSize ?? null,
        nameSize:       saved.nameSize  ?? null,
        varSize:        saved.varSize   ?? null,
        qty,
      };
    }).filter(Boolean);
  }, [selectedRows, selected, lotsCache, selectedLot, shortMap]);

  const handleOpenReview = useCallback(() => {
    if (!hasSelection || generating) return;
    setReviewOpen(true);
  }, [hasSelection, generating]);

  // Genera el PDF final aplicando los nombres/variaciones editados en la revisión.
  const handleConfirmGenerate = useCallback((edits) => {
    if (generating) return;
    setGenerating(true);
    try {
      const items = [];
      for (const r of selectedRows) {
        const qty = parseInt(selected[r.key]) || 0;
        if (qty <= 0) continue;
        const canvasEl = qrRefs.current[r.key]?.querySelector('canvas');
        if (!canvasEl) continue;
        const qrDataUrl = canvasEl.toDataURL('image/png');
        const rowLots = Array.isArray(lotsCache[r.key]) ? lotsCache[r.key] : [];
        const chosenNum = selectedLot[r.key] || (rowLots[0]?.loteNumero ?? null);
        const e = edits[r.key] || {};
        const saved = shortMap[labelKey(r.productId, r.variationId)] || {};
        const item = {
          qrDataUrl,
          productName:    (e.name || '').trim() || r.productName,
          variationLabel: (e.variation || '').trim() || r.variationLabel,
          sku:            r.sku,
          loteNumero:     chosenNum,
          price:          fmtPrice(r.price),
          layout:         saved.layout    || null,
          qrScale:        saved.qr        ?? 1,
          priceSize:      saved.priceSize ?? null,
          nameSize:       saved.nameSize  ?? null,
          varSize:        saved.varSize   ?? null,
          storeName,
          productId:      r.productId,
          variationId:    r.variationId,
        };
        for (let i = 0; i < qty; i++) items.push(item);
      }
      if (items.length === 0) { setReviewOpen(false); return; }

      generateAndDownloadLabels(items, labelConfig);

      // Marcar como etiquetadas y persistir
      const now    = new Date().toISOString();
      const newMap = { ...labeledMap };
      for (const r of selectedRows) {
        if ((parseInt(selected[r.key]) || 0) > 0) newMap[r.key] = { date: now };
      }
      setLabeledMap(newMap);
      localStorage.setItem(LS_KEY, JSON.stringify(newMap));
      setSelected({});
      setReviewOpen(false);
    } finally {
      setGenerating(false);
    }
  }, [generating, selectedRows, selected, storeName, labeledMap, lotsCache, selectedLot, labelConfig, shortMap]);

  return (
    <div className="lt-root">

      {/* Canvases de QR ocultos para todas las variaciones seleccionadas */}
      <span className="lt-qr-hidden" aria-hidden="true">
        {selectedRows.map(r => (
          <span key={r.key} ref={el => { qrRefs.current[r.key] = el; }}>
            <QRCodeCanvas
              value={makeQrValue(r.productId, r.variationId)}
              size={400}
              level="M"
              includeMargin={false}
            />
          </span>
        ))}
      </span>

      {/* Filtros */}
      <div className="lt-filters">
        <input
          className="lt-search"
          type="text"
          placeholder="Buscar por nombre, SKU o categoría…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="lt-select"
          value={catFilter}
          onChange={e => setCatFilter(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="lt-tab-group">
          {[
            { id: 'all',       label: 'Todas' },
            { id: 'unlabeled', label: 'Sin etiqueta' },
            { id: 'labeled',   label: 'Ya generadas' },
          ].map(t => (
            <button
              key={t.id}
              className={`lt-tab${labelFilter === t.id ? ' lt-tab--on' : ''}`}
              onClick={() => setLabelFilter(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Barra de herramientas */}
      <div className="lt-toolbar">
        <button
          className="lt-toolbar-btn"
          onClick={selectAll}
          disabled={filteredRows.length === 0}
        >
          Seleccionar todo lo filtrado ({filteredRows.length})
        </button>
        <span className="lt-toolbar-info">
          {rows.length} {rows.length === 1 ? 'variación' : 'variaciones'} en total
        </span>
      </div>

      {/* Barra de resumen y acciones — aparece al seleccionar */}
      <div className={`lt-actionbar${hasSelection ? ' lt-actionbar--on' : ''}`} aria-hidden={!hasSelection}>
        <div className="lt-actionbar__inner">
          <div className="lt-actionbar__info">
            <strong>{totalSelected}</strong>
            {totalSelected === 1 ? ' variación seleccionada' : ' variaciones seleccionadas'}
            <span className="lt-actionbar__dot">·</span>
            <strong>{totalLabels}</strong>
            {totalLabels === 1 ? ' etiqueta en total' : ' etiquetas en total'}
          </div>
          <div className="lt-actionbar__actions">
            <button
              className="lt-actionbar__btn lt-actionbar__btn--ghost"
              onClick={applyStockQty}
              disabled={generating}
            >
              Usar stock actual
            </button>
            <button
              className="lt-actionbar__btn lt-actionbar__btn--clear"
              onClick={clearAll}
              disabled={generating}
            >
              Limpiar selección
            </button>
            <button
              className="lt-actionbar__btn lt-actionbar__btn--generate"
              onClick={handleOpenReview}
              disabled={generating}
            >
              Revisar y generar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="lt-table-wrap">
        <table className="lt-table">
          <thead>
            <tr>
              <th className="lt-col-chk" />
              <th className="lt-col-img" />
              <th className="lt-col-name">Producto / Variación</th>
              <th className="lt-col-cat">Categoría</th>
              <th className="lt-col-price">Precio</th>
              <th className="lt-col-stock">Stock</th>
              <th className="lt-col-sku">SKU</th>
              <th className="lt-col-lot">Lote</th>
              <th className="lt-col-badge">Vista previa / Estado</th>
              <th className="lt-col-qty">Cantidad</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan="10" className="lt-empty">
                  No se encontraron variaciones con los filtros actuales.
                </td>
              </tr>
            ) : filteredRows.map(r => {
              const isChecked = selected[r.key] !== undefined;
              const isLabeled = !!labeledMap[r.key];
              const saved     = shortMap[labelKey(r.productId, r.variationId)] || {};
              const hasCustom = !!(saved.name || saved.variation || saved.layout || saved.qr || saved.priceSize || saved.nameSize || saved.varSize);
              const thumbItem = {
                qrValue:        makeQrValue(r.productId, r.variationId),
                productName:    saved.name || r.productName,
                variationLabel: saved.variation || r.variationLabel || '',
                price:          fmtPrice(r.price),
                sku:            r.sku,
                loteNumero:     selectedLot[r.key] || null,
                layout:         saved.layout    || null,
                qrScale:        saved.qr        ?? 1,
                priceSize:      saved.priceSize ?? null,
                nameSize:       saved.nameSize  ?? null,
                varSize:        saved.varSize   ?? null,
                storeName,
              };
              return (
                <tr
                  key={r.key}
                  className={`lt-row${isChecked ? ' lt-row--sel' : ''}`}
                  onClick={() => toggleRow(r.key)}
                >
                  <td className="lt-col-chk" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="lt-checkbox"
                      checked={isChecked}
                      onChange={() => toggleRow(r.key)}
                    />
                  </td>
                  <td className="lt-col-img">
                    <ImagenProducto
                      src={r.image}
                      srcset={r.imageSrcset}
                      sizes="tabla"
                      phWidth={40}
                      className="lt-img"
                    />
                  </td>
                  <td className="lt-col-name">
                    <div className="lt-name">{r.productName}</div>
                    {r.variationLabel && (
                      <div className="lt-var">{r.variationLabel}</div>
                    )}
                  </td>
                  <td className="lt-col-cat lt-muted">
                    {r.categories.join(', ') || '—'}
                  </td>
                  <td className="lt-col-price">
                    {fmtPrice(r.price) || <span className="lt-muted">—</span>}
                  </td>
                  <td className="lt-col-stock">
                    <span className={
                      r.stock <= 0 ? 'lt-stock lt-stock--none'
                      : r.stock <= 5 ? 'lt-stock lt-stock--low'
                      : 'lt-stock lt-stock--ok'
                    }>
                      {r.stock}
                    </span>
                  </td>
                  <td className="lt-col-sku lt-muted lt-sku">
                    {r.sku || '—'}
                  </td>
                  <td className="lt-col-lot" onClick={e => e.stopPropagation()}>
                    {(() => {
                      const cache = lotsCache[r.key];
                      if (!isChecked) {
                        // Sin seleccionar: solo muestra badge de lote único si está en cache
                        if (Array.isArray(cache) && cache.length === 1) {
                          return <span className="lt-lot-tag">{cache[0].loteNumero}</span>;
                        }
                        return <span className="lt-muted">—</span>;
                      }
                      if (cache === 'loading') return <span className="lt-muted">...</span>;
                      if (!Array.isArray(cache) || cache.length === 0) return <span className="lt-muted">—</span>;
                      if (cache.length === 1) {
                        return <span className="lt-lot-tag">{cache[0].loteNumero}</span>;
                      }
                      return (
                        <select
                          className="lt-lot-select"
                          value={selectedLot[r.key] || cache[0].loteNumero}
                          onChange={e => setSelectedLot(prev => ({ ...prev, [r.key]: e.target.value }))}
                        >
                          {cache.map(l => (
                            <option key={l.loteNumero} value={l.loteNumero}>
                              {l.loteNumero} ({l.quantityRemaining})
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </td>
                  <td className="lt-col-badge" onClick={e => e.stopPropagation()}>
                    <div className="lt-badge-cell">
                      <LabelThumb
                        item={thumbItem}
                        config={labelConfig}
                        custom={hasCustom}
                        onClick={() => setEditorRow(r)}
                      />
                      {isLabeled ? (
                        <span
                          className="lt-badge lt-badge--done"
                          title={`Generada el ${new Date(labeledMap[r.key].date).toLocaleDateString('es-CO')}`}
                        >
                          Etiquetada
                        </span>
                      ) : (
                        <span className="lt-badge lt-badge--none">Sin etiqueta</span>
                      )}
                    </div>
                  </td>
                  <td className="lt-col-qty" onClick={e => e.stopPropagation()}>
                    <input
                      type="number"
                      className={`lt-qty${isChecked ? ' lt-qty--on' : ''}`}
                      min="0"
                      max="999"
                      value={isChecked ? (selected[r.key] ?? 1) : ''}
                      onChange={e => setQty(r.key, e.target.value)}
                      disabled={!isChecked}
                      placeholder={isChecked ? '1' : '—'}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <LabelBatchReviewModal
        open={reviewOpen}
        rows={reviewRows}
        storeName={storeName}
        labelConfig={labelConfig}
        generating={generating}
        onCancel={() => setReviewOpen(false)}
        onConfirm={handleConfirmGenerate}
      />

      {/* Editor individual — se abre al tocar una miniatura */}
      <QRModal
        open={!!editorRow}
        onClose={() => { setEditorRow(null); setNamesVer(v => v + 1); }}
        productId={editorRow?.productId}
        variationId={editorRow?.variationId}
        productName={editorRow?.productName}
        variationLabel={editorRow?.variationLabel}
        sku={editorRow?.sku}
        price={editorRow ? fmtPrice(editorRow.price) : null}
        storeName={storeName}
        labelConfig={labelConfig}
      />
    </div>
  );
}
